import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Download, Calendar, Building2, TrendingUp, Shield, Zap, Users, BarChart3, Globe, Clock, ArrowRight, Factory, Lock, Target, GitBranch, Boxes, Package, Warehouse, ShoppingCart, Sparkles, Cpu, Check, Banknote, Brain, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const GroupCompaniesLandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    subsidiaries: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        ...formData,
        source: 'Group Companies Landing Page',
        message: `Interested in Group ERP Template. Subsidiaries: ${formData.subsidiaries}`
      });
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { value: "80,000+", label: "SAP B1 Customers Worldwide" },
    { value: "99.995%", label: "AWS Uptime Guarantee" },
    { value: "15+", label: "Years Group ERP Experience" },
    { value: "Real-time", label: "Group Consolidation" }
  ];

  const coreBenefits = [
    { 
      icon: Building2, 
      title: "One Template Per OpCo",
      strapline: "Standardised core, local flexibility",
      desc: "Deploy the same proven SAP Business One template to each subsidiary-standardised finance, purchasing, and production-while respecting unique local processes and requirements."
    },
    { 
      icon: TrendingUp, 
      title: "Faster M&A Integration",
      strapline: "Onboard acquisitions in weeks",
      desc: "New acquisitions adopt your group template immediately. Proven playbook means faster integration, lower risk, and immediate visibility into the new business."
    },
    { 
      icon: BarChart3, 
      title: "Consolidated Group Reporting",
      strapline: "Real-time group visibility",
      desc: "Automated intercompany elimination, standardised KPIs, and drill-down capability from group to subsidiary level. See the complete picture instantly."
    },
    { 
      icon: Zap, 
      title: "Reduced IT Overhead",
      strapline: "Single cloud platform",
      desc: "One AWS cloud platform for all subsidiaries means lower licensing costs, simplified support, standardised upgrades, and reduced infrastructure overhead."
    },
    { 
      icon: Factory, 
      title: "Manufacturing-Ready",
      strapline: "BOMs, MRP, production planning",
      desc: "Multi-level bills of materials, capacity-aware MRP, shop floor control, and project-driven fulfilment for complex industrial manufacturing workflows."
    },
    { 
      icon: Shield, 
      title: "Enterprise Security on AWS",
      strapline: "99.995% uptime guarantee",
      desc: "Data sovereignty with UK/EU hosting, enterprise-grade encryption, automated backups, and disaster recovery. Each subsidiary's data ringfenced with group-level access."
    }
  ];

  const extensions = [
    {
      icon: Boxes,
      title: "Beas Manufacturing",
      strapline: "Advanced shop floor control",
      desc: "Production planning, quality management, and real-time dashboards for process and discrete manufacturing. 900+ customers.",
      color: "from-blue-600 to-sky-600"
    },
    {
      icon: GitBranch,
      title: "B1 InterCompany",
      strapline: "Automated subsidiary transactions",
      desc: "Seamless inter-company transactions across multiple SAP databases with automated posting and reconciliation.",
      color: "from-green-600 to-teal-600"
    },
    {
      icon: Warehouse,
      title: "Produmex WMS",
      strapline: "Multi-site warehouse management",
      desc: "Optimise warehouse space, 99%+ picking accuracy, GS1 compliance for supply chain excellence across group locations.",
      color: "from-purple-600 to-pink-600"
    },
    {
      icon: ShoppingCart,
      title: "FocusPoint eCommerce",
      strapline: "Group-wide online sales",
      desc: "AI-powered B2B/B2C eCommerce with real-time inventory across all subsidiaries. SAP-certified platform.",
      color: "from-slate-800 to-blue-900"
    },
    {
      icon: Sparkles,
      title: "B1 Usability Package",
      strapline: "No-code customisation toolkit",
      desc: "Tailor each subsidiary's SAP instance without coding. Dashboards, workflows, and automation.",
      color: "from-green-600 to-emerald-600"
    },
    {
      icon: Cpu,
      title: "BPA Platform",
      strapline: "Group-wide process automation",
      desc: "Integrate legacy systems, automate workflows, and connect cloud/on-premise applications with 100+ connectors.",
      color: "from-indigo-600 to-violet-600"
    }
  ];

  const sectorBenefits = [
    { 
      sector: "Galvanising Networks", 
      image: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=800&q=85",
      points: ["Multi-site stock visibility", "Standardised job costing", "Capacity planning across plants", "Quality traceability"] 
    },
    { 
      sector: "Road Safety Systems", 
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=85",
      points: ["Project-driven fulfilment", "Multi-level BOMs", "Critical infrastructure compliance", "End-to-end quote-to-cash"] 
    },
    { 
      sector: "Lighting Structures", 
      image: "https://images.unsplash.com/photo-1513828583688-c52646db42da?w=800&q=85",
      points: ["Make-to-order/stock workflows", "Accurate production costing", "Inventory optimisation", "Dependable lead times"] 
    },
    { 
      sector: "Building Products", 
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=85",
      points: ["Standardised chart of accounts", "Procurement consolidation", "Stock across multiple sites", "Group purchasing power"] 
    }
  ];

  const faqs = [
    {
      q: "Is SAP Business One suitable for complex manufacturing groups?",
      a: "Absolutely. SAP B1 handles multi-level BOMs, production planning, MRP, and make-to-order/stock workflows used by industrial groups. The Hill & Smith Group case study demonstrates successful deployment across high-security fencing and steel access solutions manufacturers."
    },
    {
      q: "How do you handle intercompany transactions and consolidation?",
      a: "We implement B1 InterCompany for automated posting between subsidiaries, plus standardised chart of accounts and reporting templates for group consolidation. Real-time visibility with local autonomy maintained."
    },
    {
      q: "What about data security and AWS cloud hosting?",
      a: "Your data stays in your chosen AWS region (UK/EU) with enterprise-grade encryption, 99.995% uptime, and complete data sovereignty. GDPR and ISO 27001 compliant. Each subsidiary can access only their data with group-level reporting for head office."
    },
    {
      q: "How quickly can we see value from the pilot?",
      a: "Most groups see measurable benefits from the pilot go-live within the first quarter: improved planning accuracy, faster month-end close, and better on-time delivery. The template then accelerates subsequent rollouts by 40-50%."
    },
    {
      q: "What if we acquire new businesses?",
      a: "The group template means new acquisitions can be onboarded in weeks rather than months. Hill & Smith now has a repeatable playbook for faster, lower-risk integration of future businesses."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Centered Glass Panel Style */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img src="/group-companies-hero.jpeg" alt="Industrial Group Operations" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-950/70 to-slate-900/80" />
        </div>
        
        {/* Centered Content */}
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6 text-white">SAP Business One on AWS for Industrial Groups</div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Scale Your Industrial Group with Confidence
              </h1>
              
              <p className="text-xl md:text-2xl text-sky-300 mb-8 font-medium max-w-3xl mx-auto">
                Faster M&A onboarding. Standardised finance and operations. Local autonomy maintained. Group-wide visibility and control.
              </p>
              
              {/* Glass Panel with Key Features */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-8 max-w-3xl mx-auto">
                <p className="text-white font-semibold mb-6 text-lg">Built for multi-subsidiary industrial groups</p>
                <div className="grid md:grid-cols-2 gap-4 text-left">
                  {[
                    { icon: CheckCircle2, text: "One SAP Business One template per operating company" },
                    { icon: CheckCircle2, text: "Standardised finance with local operational autonomy" },
                    { icon: CheckCircle2, text: "Faster M&A integration and subsidiary onboarding" },
                    { icon: CheckCircle2, text: "Consolidated group reporting and intercompany automation" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-1" />
                      <span className="text-white text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <a href="#contact">
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-5 text-lg font-semibold shadow-xl">
                    <Calendar className="w-5 h-5 mr-2" />
                    Speak to a Specialist
                  </Button>
                </a>
              </div>

              <p className="text-blue-200 text-sm">
                See how industrial groups achieve faster M&A integration with standardised SAP Business One on AWS
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hill & Smith Proof Panel with Image */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch mb-12">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=85" alt="Industrial Manufacturing" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-green-600/30 to-transparent" />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4 w-fit">Proven Results</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hill & Smith Group Success Story</h2>
              <p className="text-lg text-gray-600 mb-6">Standardised SAP Business One across Barkers (high-security fencing) and Lionweld (steel access solutions)</p>
              
              <div className="space-y-3">
                {[
                  "Single system of record across all subsidiaries",
                  "Improved planning accuracy and supplier alignment",
                  "Better on-time delivery with constraint visibility",
                  "Shorter month-end close via automation",
                  "Higher productivity-no duplicate data entry",
                  "Repeatable template for future rollouts"
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="p-8 bg-white rounded-2xl border-2 border-green-200 shadow-lg">
              <p className="text-lg text-gray-700 italic mb-4">
                "The result is a single, trusted system of record that improves planning accuracy, on-time delivery, and decision-making. With a stable core in place, Hill & Smith can extend common analytics and continue the template rollout to other group businesses at lower cost and risk."
              </p>
              <p className="text-sm text-gray-600">- Hill & Smith Group Case Study</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Benefits - SAP B1 Showcase Style */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-800 via-blue-900 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Multi-Subsidiary Groups Choose This Approach</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Standardisation without losing operational flexibility</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreBenefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-sm text-cyan-300 font-medium mb-2">{benefit.strapline}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAP B1 Core Strengths - Myth Busting Style */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why SAP Business One for Your Group?</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Designed specifically for growing businesses and industrial groups</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: "Simplicity", strapline: "Less learning, more doing", desc: "Easy-to-use interface means your teams across all subsidiaries spend less time learning and more time doing." },
              { icon: Zap, title: "Fast to Deploy", strapline: "Minimal disruption", desc: "Pilot OpCo operational within 12-16 weeks. Subsequent rollouts even faster with proven template." },
              { icon: Banknote, title: "Affordable at Scale", strapline: "Group licensing advantages", desc: "Enterprise-grade ERP without enterprise price tag. Group licensing reduces per-seat costs." },
              { icon: Brain, title: "AI Ready", strapline: "Future-proof technology", desc: "Harness AI-powered insights across your group to make data-driven decisions that propel growth." },
              { icon: Award, title: "SAP Strategic Platform", strapline: "Long-term commitment", desc: "Trusted by 1,000+ global partners. SAP's strategic platform for SMEs and growing groups." },
              { icon: Globe, title: "Global Group Solution", strapline: "28 languages, 170 countries", desc: "Perfect for groups with international subsidiaries. Multi-language, multi-currency as standard." }
            ].map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group flex items-start gap-4 p-6 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-400 transition-all">
                  <benefit.icon className="w-7 h-7 text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-sm text-cyan-300 font-medium mb-2">{benefit.strapline}</p>
                  <p className="text-blue-100 text-sm leading-relaxed">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sector-Specific Benefits with Images */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Built for Your Industry</h2>
            <p className="text-xl text-gray-600">Proven across industrial manufacturing groups</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {sectorBenefits.map((sector, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="group rounded-2xl overflow-hidden border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="relative h-48 overflow-hidden">
                  <img src={sector.image} alt={sector.sector} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-6">
                    <h3 className="text-2xl font-bold text-white">{sector.sector}</h3>
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <ul className="space-y-2">
                    {sector.points.map((point, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SAP B1 Key Modules - Alternating Sections */}
      
      {/* Analytics & Intelligence for Groups */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=85" alt="Analytics" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4 w-fit">Analytics & Intelligence</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Group-Wide Insights in Real-Time</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Consolidated dashboards across all subsidiaries with drill-down capability. SAP HANA in-memory computing delivers instant group reporting and business intelligence.</p>
              <div className="space-y-3">
                {["Group consolidation with intercompany elimination", "Drill-down from group to subsidiary level", "Real-time dashboards for each OpCo", "SAP Crystal Reports with Excel integration", "Standardised KPIs across all businesses"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Accounting & Financials for Groups */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4 w-fit">Group Finance</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Standardised Finance Across All Subsidiaries</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Common chart of accounts, automated intercompany postings, and group consolidation. Each subsidiary maintains local compliance while feeding into group reporting.</p>
              <div className="space-y-3">
                {["Standardised chart of accounts across group", "Automated intercompany transactions", "Multi-currency with consolidation rates", "Making Tax Digital (MTD) compliant", "Faster month-end close with automation"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full max-h-[450px]">
                <img src="https://images.pexels.com/photos/8927463/pexels-photo-8927463.jpeg?w=800&auto=compress" alt="Financial Management" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-green-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Production & MRP for Manufacturing Groups */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?w=800&q=85" alt="Production" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-br from-rose-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-4 w-fit">Production & MRP</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Production Planning Across Your Group</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Multi-level BOMs, production planning, and MRP for capacity-aware planning. Perfect for make-to-order and make-to-stock industrial manufacturing.</p>
              <div className="space-y-3">
                {["Multi-level bills of materials and routings", "Capacity-aware MRP and material planning", "Production dashboards per subsidiary", "Integration with Beas Manufacturing", "Make-to-order and make-to-stock workflows"].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SAP B1 Extensions - Showcase Style */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">Powerful Extensions</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Enhance Your Group Template</h2>
            <p className="text-xl text-gray-600">Purpose-built extensions for industrial manufacturing groups</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {extensions.map((ext, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group bg-gradient-to-br from-slate-50 to-white rounded-xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="flex items-start gap-6">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${ext.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                    <ext.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{ext.title}</h3>
                    <p className="text-sm text-blue-600 font-medium mb-3">{ext.strapline}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{ext.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Common Questions from Group IT & Finance Directors</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* High-Converting CTA Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Transform Your Group's ERP Strategy</h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Stop struggling with disconnected systems across your subsidiaries. Get the Hill & Smith Group playbook for standardised, scalable ERP that accelerates M&A integration.
            </p>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-12 border border-white/20">
              <p className="text-xl text-white font-semibold mb-4">
                Book your 20-minute discovery call today and discover:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
                {[
                  "How to deploy a proven group template in 12-16 weeks",
                  "Real costs and ROI for multi-subsidiary rollouts",
                  "Intercompany automation and consolidation approach",
                  "How Hill & Smith achieved faster month-end and better planning"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-cyan-300 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <a href="/contact" className="inline-flex items-center justify-center rounded-xl bg-white text-blue-600 px-12 py-6 text-2xl font-bold hover:bg-blue-50 shadow-2xl transition-all hover:scale-105 group">
                <Calendar className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" />
                Get in Touch Today
              </a>
            </div>
            
            <p className="text-blue-100 text-lg mb-8">
              No obligations • Free 20-minute consultation • Speak with a group ERP specialist
            </p>
            
            <div className="flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">SAP Premier Partner</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">Hill & Smith Group Success</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">15+ Years Experience</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GroupCompaniesLandingPage;
